import { memberCardSchema, type MemberCard } from '@growup/accounts-shared';
import { Button, Card, downloadPlayerCard, HofMedal, Notice, PlayerCard } from '@growup/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getJson, messageOf, request, send } from '../api';

/**
 * A carta de membro na ficha. A foto so vem para quem pode ler o perfil (de um
 * menor, so administradores); para os outros, silhueta. Quem tem
 * `members.manage` pode tirar uma foto que nao devia la estar.
 */
export function MemberCardView({
  accountId,
  canManage,
}: {
  accountId: string;
  canManage: boolean;
}) {
  const base = `/api/members/members/${accountId}`;
  const [card, setCard] = useState<MemberCard | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const svg = useRef<SVGSVGElement>(null);

  const load = useCallback(async () => {
    try {
      const next = await getJson(`${base}/card`, memberCardSchema);
      setCard(next);
      setPhotoUrl(
        next.photo && next.photoVersion
          ? URL.createObjectURL(
              await (
                await request('GET', `${base}/photo?v=${encodeURIComponent(next.photoVersion)}`)
              ).blob(),
            )
          : null,
      );
      setError(null);
    } catch (e) {
      setError(messageOf(e));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => () => (photoUrl ? URL.revokeObjectURL(photoUrl) : undefined), [photoUrl]);

  async function act(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  if (!card) return error ? <Notice tone="danger">{error}</Notice> : null;

  return (
    <Card headingLevel={2} title="Carta de membro">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <PlayerCard
          ref={svg}
          name={card.login}
          tier={card.level}
          streamer={card.streamer}
          number={card.memberNumber}
          photoUrl={photoUrl}
          stats={[
            { label: 'Hrs Arena', value: card.stats.arenaHours },
            { label: 'Eventos', value: card.stats.events },
            { label: 'Anos', value: card.stats.years },
          ]}
          years={card.stats.years}
          medals={card.awards.map((a) => ({ icon: a.icon, title: `${a.category} · ${a.edition}` }))}
          width={200}
          className="shrink-0"
        />
        <div className="flex flex-col gap-3 text-gu-ink-muted">
          <p>
            {card.photo
              ? 'Foto posta pelo próprio ou pelo encarregado. Cada vez que a vês fica registado.'
              : 'Sem foto, ou foto reservada (menor de idade: só os administradores a veem).'}
          </p>
          {card.awards.length > 0 ? (
            <div className="flex flex-col gap-1">
              <p className="font-sub text-label font-bold uppercase text-gu-ink">
                Prémios Grow uP (Hall of Fame)
              </p>
              <ul className="flex flex-col gap-1">
                {card.awards.map((a, i) => (
                  <li
                    key={`${a.category}-${a.edition}-${String(i)}`}
                    className="flex items-center gap-2"
                  >
                    <HofMedal icon={a.icon} size={22} />
                    <span className="text-gu-ink">{a.category}</span>
                    <span>· {a.edition}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  if (svg.current) await downloadPlayerCard(svg.current, `carta-${card.login}`);
                })
              }
            >
              Descarregar imagem
            </Button>
            {canManage && card.photo ? (
              <Button
                variant="danger"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await send('DELETE', `${base}/photo`);
                    await load();
                  })
                }
              >
                Tirar foto
              </Button>
            ) : null}
          </div>
          {error ? <Notice tone="danger">{error}</Notice> : null}
        </div>
      </div>
    </Card>
  );
}
